import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Switch,
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as UploadIcon, Public as PublicIcon, Lock as PrivateIcon } from '@mui/icons-material';
import { useFilestoreFiles, useDeleteFilestoreFile, useUploadToFilestore, useUpdateFilestoreFile, FilestoreFile } from '../api/filestore';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface FilestoreBrowserProps {
  open: boolean;
  onClose: () => void;
  onSelectFile: (file: FilestoreFile) => void;
  acceptedFileTypes?: string;
  currentUsername?: string;
}

const FilestoreBrowser: React.FC<FilestoreBrowserProps> = ({
  open,
  onClose,
  onSelectFile,
  acceptedFileTypes = '.zip,.kml,.geojson,.json',
  currentUsername,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: number; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  
  const { files, isLoading, error } = useFilestoreFiles(searchQuery);
  const deleteMutation = useDeleteFilestoreFile();
  const uploadMutation = useUploadToFilestore();
  const updateMutation = useUpdateFilestoreFile();
  
  const currentUser = currentUsername || user?.username;

  const handleDelete = (fileId: number, filename: string) => {
    setFileToDelete({ id: fileId, name: filename });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete) {
      try {
        await deleteMutation.mutateAsync(fileToDelete.id);
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleTogglePublic = async (fileId: number, isPublic: boolean) => {
    try {
      await updateMutation.mutateAsync({ fileId, isPublic: !isPublic });
    } catch (err) {
      console.error('Error updating file visibility:', err);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    try {
      const result = await uploadMutation.mutateAsync({ 
        file, 
        uploadedFrom: 'filestore_browser' 
      });
      
      // Auto-select the newly uploaded file
      if (result?.data) {
        onSelectFile(result.data);
        onClose();
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to upload file');
      console.error('Error uploading file:', err);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Select from My Files</DialogTitle>
        <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Shapefiles must be uploaded as a .zip archive.
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Search files"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
            disabled={uploadMutation.isPending}
          >
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading files: {error.message || 'Unknown error'}
          </Alert>
        )}

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        {uploadMutation.isPending && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Uploading file...
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {searchQuery ? 'No files found matching your search.' : 'No files in your filestore yet.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180, maxWidth: 300 }}>Filename</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Upload Date</TableCell>
                <TableCell sx={{ minWidth: 70 }}>Size</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Visibility</TableCell>
                <TableCell sx={{ minWidth: 60 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => (
                <TableRow
                  key={file.id}
                  hover
                  onClick={() => {
                    onSelectFile(file);
                    onClose();
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 300
                        }}
                        title={file.original_filename}
                      >
                        {file.original_filename}
                      </Typography>
                      {file.is_public && currentUser && file.username !== currentUser && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          by {file.username}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(file.upload_date)}</TableCell>
                  <TableCell>{formatFileSize(file.file_size)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={file.is_public ? 'Public - visible to all users' : 'Private - only visible to you'}>
                        {file.is_public ? (
                          <PublicIcon fontSize="small" sx={{ fontSize: 14 }} color="success" />
                        ) : (
                          <PrivateIcon fontSize="small" sx={{ fontSize: 14 }} color="disabled" />
                        )}
                      </Tooltip>
                      <Switch
                        size="small"
                        checked={file.is_public}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleTogglePublic(file.id, file.is_public);
                        }}
                        disabled={updateMutation.isPending}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: file.is_public ? 'success.main' : 'text.secondary',
                          fontWeight: file.is_public ? 600 : 400,
                          minWidth: 45
                        }}
                      >
                        {file.is_public ? 'Public' : 'Private'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {currentUser && file.username === currentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file.id, file.original_filename);
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      </Dialog>

      <ConfirmDeleteModal
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        fileName={fileToDelete?.name || ''}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default FilestoreBrowser;
